/**
 * Workspace-Complete Restore hooks (Spec 205)
 *
 * React Query reads (candidates / definitions / diff) + launch mutation.
 *
 * Author: Peter Dobler (Dobler Data Solutions)
 * Feature: 205-workspace-complete-dr
 */

import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { workspaceRestoreApi, WorkspaceRestoreLaunchInput } from '../api/workspaceRestore';

export const workspaceRestoreKeys = {
  all: ['workspace-restore'] as const,
  candidates: () => [...workspaceRestoreKeys.all, 'candidates'] as const,
  definitions: (wsId: string) => [...workspaceRestoreKeys.all, 'definitions', wsId] as const,
  pointItems: (wsId: string, runId: string) =>
    [...workspaceRestoreKeys.all, 'point-items', wsId, runId] as const,
  diff: (wsId: string, from: string, to: string, item: string) =>
    [...workspaceRestoreKeys.all, 'diff', wsId, from, to, item] as const,
};

export function useWorkspaceRestoreCandidates() {
  return useQuery({
    queryKey: workspaceRestoreKeys.candidates(),
    queryFn: () => workspaceRestoreApi.candidates(),
    staleTime: 60_000,
  });
}

export function useWorkspaceDefinitions(workspaceId: string | null) {
  return useQuery({
    queryKey: workspaceRestoreKeys.definitions(workspaceId ?? ''),
    queryFn: () => workspaceRestoreApi.definitions(workspaceId as string),
    enabled: Boolean(workspaceId),
  });
}

export function useDefinitionDiff(
  workspaceId: string | null,
  fromRunId: string | null,
  toRunId: string | null,
  itemId: string | null
) {
  return useQuery({
    queryKey: workspaceRestoreKeys.diff(workspaceId ?? '', fromRunId ?? '', toRunId ?? '', itemId ?? ''),
    queryFn: () =>
      workspaceRestoreApi.diff(workspaceId as string, fromRunId as string, toRunId as string, itemId as string),
    enabled: Boolean(workspaceId && fromRunId && toRunId && itemId),
  });
}

export function useLaunchWorkspaceRestore() {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: (input: WorkspaceRestoreLaunchInput) => workspaceRestoreApi.launch(input),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: workspaceRestoreKeys.candidates() });
    },
  });
}

/** Explore the contents of a specific recovery point (the captured items at that point). */
export function useDefinitionPointItems(workspaceId: string | null, runId: string | null) {
  return useQuery({
    queryKey: workspaceRestoreKeys.pointItems(workspaceId ?? '', runId ?? ''),
    queryFn: () => workspaceRestoreApi.pointItems(workspaceId as string, runId as string),
    enabled: Boolean(workspaceId && runId),
  });
}

/** Selective / granular restore — bring back chosen items as NEW items (renamed on collision). */
export function useRestoreItems() {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: (input: {
      workspaceId: string;
      definitionRunId: string;
      itemIds: string[];
      targetWorkspaceId?: string;
      createTempWorkspace?: boolean;
    }) => workspaceRestoreApi.restoreItems(input),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: workspaceRestoreKeys.candidates() });
    },
  });
}
